import { Download, ErrorOutline, InsertDriveFileOutlined, Refresh } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ChatAttachment } from '@/models/GroupChatMessage';
import { generateChatAttachmentUrl } from '@/services/api/clientGroupChat';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface ChatAttachmentDisplayProps {
  attachment: ChatAttachment;
  groupId: string;
  isOwn: boolean;
}

const ChatAttachmentDisplay = ({ attachment, groupId, isOwn }: ChatAttachmentDisplayProps) => {
  const isImage = attachment.mimeType.startsWith('image/');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  const {
    data: presignedUrl,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['chatAttachmentUrl', groupId, attachment.s3Key],
    queryFn: () => generateChatAttachmentUrl(groupId, attachment.s3Key),
    staleTime: 50 * 60 * 1000, // 50 minutes (URL valid for 60)
    enabled: Boolean(groupId && attachment.s3Key && isImage),
    retry: 1,
  });

  const handleDownload = async () => {
    if (presignedUrl) {
      window.open(presignedUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // For PDFs, fetch URL lazily on click
    if (!isImage) {
      setPdfDownloading(true);
      setPdfError(false);
      try {
        const { data } = await refetch();
        if (data) {
          window.open(data, '_blank', 'noopener,noreferrer');
        }
      } catch {
        setPdfError(true);
      } finally {
        setPdfDownloading(false);
      }
    }
  };

  const errorColor = isOwn ? 'rgba(38,38,38,0.5)' : 'rgba(243,243,243,0.4)';

  if (isImage) {
    if (isError) {
      return (
        <Box
          onClick={() => refetch()}
          sx={{
            mt: 0.5,
            width: 200,
            height: 80,
            borderRadius: '8px',
            backgroundColor: isOwn ? 'var(--tx-fill)' : 'var(--tx-fill)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            gap: 0.5,
          }}>
          <ErrorOutline sx={{ fontSize: 18, color: errorColor }} />
          <Typography sx={{ fontSize: 11, color: errorColor }}>
            Failed to load — tap to retry
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        onClick={handleDownload}
        sx={{
          mt: 0.5,
          maxWidth: 300,
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: presignedUrl ? 'pointer' : 'default',
        }}>
        {isLoading || !presignedUrl ? (
          <Skeleton variant="rectangular" width={200} height={140} sx={{ borderRadius: '8px' }} />
        ) : (
          <Box
            component="img"
            src={presignedUrl}
            alt={attachment.filename}
            onError={() => refetch()}
            sx={{
              width: '100%',
              maxHeight: 300,
              objectFit: 'cover',
              display: 'block',
              borderRadius: '8px',
            }}
          />
        )}
      </Box>
    );
  }

  // PDF / other file type
  return (
    <Box
      sx={{
        mt: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        px: '10px',
        py: '8px',
        borderRadius: '8px',
        backgroundColor: isOwn ? 'var(--tx-fill)' : 'var(--tx-fill)',
        maxWidth: 260,
      }}>
      <InsertDriveFileOutlined
        sx={{
          fontSize: 26,
          color: isOwn ? 'rgba(38,38,38,0.85)' : 'var(--tx-text)',
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: isOwn ? 'var(--tx-text)' : 'var(--tx-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {attachment.filename}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            color: isOwn ? 'rgba(38,38,38,0.5)' : 'var(--tx-faint)',
          }}>
          {pdfError ? 'Failed to load' : formatFileSize(attachment.fileSize)}
        </Typography>
      </Box>
      <IconButton
        onClick={handleDownload}
        disabled={pdfDownloading}
        size="small"
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          '&:hover': {
            backgroundColor: isOwn ? 'var(--tx-fill)' : 'var(--tx-fill-2)',
          },
        }}>
        {pdfDownloading ? (
          <CircularProgress
            size={14}
            sx={{ color: isOwn ? 'rgba(38,38,38,0.4)' : 'rgba(243,243,243,0.4)' }}
          />
        ) : pdfError ? (
          <Refresh
            sx={{
              fontSize: 16,
              color: isOwn ? 'rgba(38,38,38,0.6)' : 'var(--tx-faint)',
            }}
          />
        ) : (
          <Download
            sx={{
              fontSize: 16,
              color: isOwn ? 'rgba(38,38,38,0.6)' : 'var(--tx-faint)',
            }}
          />
        )}
      </IconButton>
    </Box>
  );
};

export default ChatAttachmentDisplay;
